import { useMemo } from "react";

import { Controller, useFieldArray, useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, Plus, Trash2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  SearchableSelect,
  type SearchableSelectGroup,
} from "~/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import {
  getStockAt,
  useCreateIngredientTransfer,
  useOilTypeStockByPlant,
  useOilTypes,
  useRawMaterialStockByPlant,
  useRawMaterials,
} from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import { useProfileStore } from "~/store/profileStore";
import { createTransferSchema } from "~/types/ingredientTransfer";
import { TRANSFER_PLANT_OPTIONS } from "~/types/purchasing";

import type { CreateTransferFormData } from "~/types/ingredientTransfer";

interface IngredientTransferFormProps {
  onDone: () => void;
}

const fmtQty = (n: number) =>
  n.toLocaleString("es-CL", { maximumFractionDigits: 2 });

// Move oils + raw materials (insumos y materias primas) between plants P1 <-> P2.
// Multi-item: add one or more ingredients, each with its own quantity.
export function IngredientTransferForm({ onDone }: IngredientTransferFormProps) {
  const { toast } = useToast();
  const { id: userId } = useProfileStore();
  const createTransfer = useCreateIngredientTransfer();

  const { data: oilTypes = [] } = useOilTypes();
  const { data: rawMaterials = [] } = useRawMaterials();
  const { data: oilStockByPlant } = useOilTypeStockByPlant();
  const { data: rawStockByPlant } = useRawMaterialStockByPlant();

  // Oils are stored in liters but received/handled in kg. Convert with each
  // oil's density (kg per liter): kg = liters * density, liters = kg / density.
  const oilDensity = useMemo(
    () => new Map(oilTypes.map((o) => [o.id, o.density_kg_per_liter || 1])),
    [oilTypes]
  );

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateTransferFormData>({
    resolver: zodResolver(createTransferSchema),
    defaultValues: { from_plant: "P1", to_plant: "P2", notes: "", items: [] },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "items" });

  const fromPlant = watch("from_plant");
  const itemsWatch = watch("items");

  // All stock-bearing ingredients, oils first (excluding materials kept out of inventory).
  const options = useMemo(() => {
    const oils = oilTypes.map((o) => ({
      key: `oil:${o.id}`,
      kind: "oil" as const,
      id: o.id,
      name: o.name,
      code: o.code,
      unit: "kg",
    }));
    const materials = rawMaterials
      .filter((r) => !r.exclude_from_inventory)
      .map((r) => ({
        key: `raw_material:${r.id}`,
        kind: "raw_material" as const,
        id: r.id,
        name: r.name,
        code: r.code,
        unit: r.unit,
      }));
    return [...oils, ...materials];
  }, [oilTypes, rawMaterials]);

  const addedIds = new Set(
    fields.map((f) => f.oil_type_id ?? f.raw_material_id ?? "")
  );
  const availableOptions = options.filter((o) => !addedIds.has(o.id));

  // Grouped, searchable options for the "Agregar ingrediente" picker.
  const ingredientGroups: SearchableSelectGroup[] = (() => {
    const toOption = (o: (typeof options)[number]) => ({
      value: o.key,
      label: `${o.name} (${o.code})`,
      searchTerms: `${o.name} ${o.code}`,
    });
    const oils = availableOptions
      .filter((o) => o.kind === "oil")
      .map(toOption);
    const materials = availableOptions
      .filter((o) => o.kind === "raw_material")
      .map(toOption);
    const groups: SearchableSelectGroup[] = [];
    if (oils.length) groups.push({ label: "Aceites", options: oils });
    if (materials.length)
      groups.push({ label: "Materia prima", options: materials });
    return groups;
  })();

  // Available stock at the origin plant, in the unit shown to the user:
  // kg for oils (liters * density), native unit for raw materials.
  const availableFor = (
    ingredientType: "oil" | "raw_material",
    id: string | null
  ) => {
    if (!id) return 0;
    const stock = getStockAt(
      ingredientType === "oil" ? oilStockByPlant : rawStockByPlant,
      id,
      fromPlant
    );
    return ingredientType === "oil" ? stock * (oilDensity.get(id) ?? 1) : stock;
  };

  const hasExceed = (itemsWatch ?? []).some(
    (it) =>
      Number(it.quantity) >
      availableFor(it.ingredient_type, it.oil_type_id ?? it.raw_material_id)
  );

  const handleAdd = (value: string) => {
    const opt = options.find((o) => o.key === value);
    if (!opt) return;
    append({
      ingredient_type: opt.kind,
      oil_type_id: opt.kind === "oil" ? opt.id : null,
      raw_material_id: opt.kind === "raw_material" ? opt.id : null,
      name: opt.name,
      quantity: 0,
      unit: opt.unit,
    });
  };

  const onSubmit = (data: CreateTransferFormData) => {
    if (!userId) {
      toast({
        title: "Error",
        description: "No se pudo identificar al usuario.",
        variant: "destructive",
      });
      return;
    }
    const exceeds = data.items.some(
      (it) =>
        it.quantity >
        availableFor(it.ingredient_type, it.oil_type_id ?? it.raw_material_id)
    );
    if (exceeds) {
      toast({
        title: "Error",
        description:
          "Una o más cantidades exceden el stock disponible en la planta de origen.",
        variant: "destructive",
      });
      return;
    }

    createTransfer.mutate(
      {
        fromPlant: data.from_plant,
        toPlant: data.to_plant,
        items: data.items.map((it) => {
          // Oils are entered in kg but the RPC/stock work in liters -> convert.
          const isOil = it.ingredient_type === "oil";
          const density = isOil
            ? oilDensity.get(it.oil_type_id ?? "") ?? 1
            : 1;
          return {
            ingredient_type: it.ingredient_type,
            oil_type_id: it.oil_type_id,
            raw_material_id: it.raw_material_id,
            quantity: isOil ? it.quantity / density : it.quantity,
            unit: isOil ? "L" : it.unit,
          };
        }),
        notes: data.notes?.trim() ? data.notes.trim() : null,
        performedBy: userId,
      },
      {
        onSuccess: (res) => {
          toast({
            title: "Traslado creado",
            description: `Guía ${res.transfer_number} registrada correctamente.`,
          });
          onDone();
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Mueve aceites y materia prima entre plantas (P1, P2) y la bodega No
        Conforme (NC). Al confirmar se descuenta del origen y se suma al destino
        de inmediato. El stock en No Conforme no se cuenta en el total disponible
        ni se consume en producción.
      </p>

      {/* Plants */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1 space-y-1.5">
          <Label>Origen</Label>
          <Controller
            control={control}
            name="from_plant"
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={(v) => {
                  field.onChange(v);
                  if (v === watch("to_plant")) {
                    const alt = TRANSFER_PLANT_OPTIONS.find(
                      (p) => p.value !== v
                    );
                    if (alt) setValue("to_plant", alt.value);
                  }
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRANSFER_PLANT_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <ArrowRight className="mx-auto hidden h-4 w-4 shrink-0 text-muted-foreground sm:mb-2.5 sm:block" />
        <div className="flex-1 space-y-1.5">
          <Label>Destino</Label>
          <Controller
            control={control}
            name="to_plant"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRANSFER_PLANT_OPTIONS.filter(
                    (p) => p.value !== fromPlant
                  ).map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      </div>

      {/* Add ingredient */}
      <div className="space-y-1.5">
        <Label>Agregar ingrediente</Label>
        <SearchableSelect
          value=""
          onValueChange={handleAdd}
          groups={ingredientGroups}
          placeholder="Buscar aceite o materia prima…"
          searchPlaceholder="Nombre o código…"
          emptyMessage={
            availableOptions.length === 0
              ? "No hay más ingredientes para agregar"
              : "No se encontraron ingredientes"
          }
          className="[&_input]:h-9"
        />
      </div>

      {/* Item lines */}
      {fields.length === 0 ? (
        <div className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
          Agrega un ingrediente arriba; luego ingresa la cantidad a trasladar en
          cada línea.
        </div>
      ) : (
        <div className="space-y-2">
          {fields.map((field, index) => {
            const id = field.oil_type_id ?? field.raw_material_id;
            const available = availableFor(field.ingredient_type, id);
            const qty = Number(itemsWatch?.[index]?.quantity) || 0;
            const exceeds = qty > available;
            return (
              <div
                key={field.id}
                className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/30 p-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{field.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Disponible en {fromPlant}: {fmtQty(available)} {field.unit}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    className="h-9 w-28"
                    placeholder="Cantidad"
                    {...register(`items.${index}.quantity`, {
                      valueAsNumber: true,
                    })}
                  />
                  <span className="w-8 text-sm text-muted-foreground">
                    {field.unit}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0 text-muted-foreground hover:text-red-700"
                    onClick={() => remove(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {exceeds && (
                  <p className="w-full text-xs text-red-700">
                    Excede el stock disponible en {fromPlant} (
                    {fmtQty(available)} {field.unit}).
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
      {errors.items?.message && (
        <p className="text-xs text-red-700">{errors.items.message}</p>
      )}
      {errors.to_plant?.message && (
        <p className="text-xs text-red-700">{errors.to_plant.message}</p>
      )}

      {/* Notes */}
      <div className="space-y-1.5">
        <Label htmlFor="ingredient-transfer-notes">Notas (opcional)</Label>
        <Textarea
          id="ingredient-transfer-notes"
          rows={2}
          placeholder="Motivo o referencia del traslado"
          {...register("notes")}
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onDone}>
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={createTransfer.isPending || fields.length === 0 || hasExceed}
        >
          {createTransfer.isPending ? (
            "Procesando…"
          ) : (
            <>
              <Plus className="mr-1 h-4 w-4" />
              Crear traslado
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
